import React from "react";
import { FaFilePdf } from "react-icons/fa";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import { Cliente, Reserva } from "../../types";
import { formatNumeroReserva } from "../../utils/reservaUtils";
import { getUserProfile } from "../../services/userService";
import { Button } from "../../app/components";
import { FileText } from "lucide-react";

interface RelatorioButtonProps {
  reserva: Reserva;
  cliente: Cliente | null;
  className?: string;
}

const formatDate = (date?: string | Date) =>
  date ? new Date(date).toLocaleDateString("pt-BR") : "-";

const formatCurrency = (v?: number) =>
  (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Função auxiliar para obter label da forma de pagamento
const getFormaPagamentoLabel = (forma?: string) => {
  if (!forma) return "Não informado";
  const formas: { [key: string]: string } = {
    dinheiro: "Dinheiro",
    pix: "PIX",
    cartao_debito: "Cartão Débito",
    cartao_credito: "Cartão Crédito",
    transferencia: "Transferência",
  };
  return formas[forma] || forma;
};

const addSection = (
  doc: jsPDF,
  title: string,
  y: number,
  m: number,
  w: number
) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(title.toUpperCase(), m, y);
  y += 3;
  doc.line(m, y, w - m, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  return y + 4;
};

// 🔹 Busca a mensagem personalizada do perfil do usuário
const getMensagemPerfil = async (): Promise<string> => {
  try {
    const perfil = await getUserProfile();
    return perfil.success && perfil.data?.mensagemRelatorio
      ? perfil.data.mensagemRelatorio.slice(0, 1300)
      : "";
  } catch {
    return "";
  }
};
const getLogoDataURL = async (): Promise<{
  dataURL: string;
  type: "PNG" | "JPEG";
  width: number;
  height: number;
} | null> => {
  try {
    const perfil = await getUserProfile();
    const url = perfil.success ? perfil.data?.contractLogoURL : null;

    if (!url) return null;

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // 🔑 MUITO IMPORTANTE
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(null);

        ctx.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL("image/png");

        resolve({
          dataURL,
          type: "PNG",
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onerror = (e) => {
        console.error("[LOGO] Erro ao carregar imagem", e);
        reject(null);
      };
    });
  } catch (e) {
    console.error("[LOGO] Erro geral", e);
    return null;
  }
};

const RelatorioButton: React.FC<RelatorioButtonProps> = ({
  reserva,
  cliente,
  className,
}) => {
  const generatePDF = async () => {
    const doc = new jsPDF();
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const headerHeight = 30;
    const m = 15;
    let y = 15;

    try {
      const logoData = await getLogoDataURL();
      if (logoData) {
        const maxW = 60;
        const maxH = 40;

        const ratio = Math.min(maxW / logoData.width, maxH / logoData.height);

        const drawW = logoData.width * ratio;
        const drawH = logoData.height * ratio;

        // ✅ CENTRALIZADO
        const logoX = (w - drawW) / 2;
        const logoY = (headerHeight - drawH) / 2 + 5;

        doc.addImage(
          logoData.dataURL,
          logoData.type,
          logoX,
          logoY,
          drawW,
          drawH
        );

        y = headerHeight + 5;
      }
    } catch (e) {
      // segue sem logo
    }

    const numero = formatNumeroReserva(
      reserva.numeroReserva || reserva.id?.slice(-6) || ""
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);

    y += 15;

    // Título à esquerda
    doc.text("CONTRATO DE RESERVA", m, y);

    // Número do contrato à direita
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Contrato Nº ${numero}`, w - m, y, { align: "right" });

    y += 2;

    doc.line(m, y, w - m, y);
    y += 6;

    // 🔹 Contratante + Reserva (2 colunas)
    const col1 = m;
    const col2 = w / 2 + 5;
    // y = addSection(doc, "Contratante / Reserva", y, m, w);

    doc.text(`Nome: ${cliente?.nome || reserva.clienteNome || "-"}`, col1, y);
    doc.text(`Status: ${reserva.status}`, col2, y);
    y += 5;

    doc.text(`Telefone: ${cliente?.telefone || "-"}`, col1, y);
    doc.text(`Retirada: ${formatDate(reserva.dataRetirada)}`, col2, y);
    y += 5;

    doc.text(`CPF: ${cliente?.cpf || "-"}`, col1, y);
    doc.text(`Devolução: ${formatDate(reserva.dataDevolucao)}`, col2, y);
    y += 8;

    // 🔹 Objetos Locados (compacto)
    y = addSection(doc, "Objetos Locados", y, m, w);

    if (reserva.produtos?.length) {
      reserva.produtos.forEach((p, i) => {
        // Verificar se precisa quebrar página
        if (y > h - 30) {
          doc.addPage();
          y = m;
        }

        const linha = `${i + 1}) ${p.produtoTitulo} | Tam: ${
          p.produtoTamanho || "-"
        } | Cor: ${p.produtoCor || "-"} | Qtd: ${p.quantidade}`;
        doc.text(linha, m, y);
        y += 4;

        // Adicionar observações do produto se existirem
        if (p.produtoObservacoes && p.produtoObservacoes.trim()) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);

          // Quebrar texto longo em múltiplas linhas se necessário
          const obsLines = doc.splitTextToSize(
            `   Obs: ${p.produtoObservacoes}`,
            w - m * 2 - 10
          );
          obsLines.forEach((line: string) => {
            if (y > h - 30) {
              doc.addPage();
              y = m;
            }
            doc.text(line, m + 5, y);
            y += 3.5;
          });

          // Restaurar estilo padrão
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);
          y += 2;
        }
      });
    } else {
      doc.text("Nenhum item informado", m, y);
      y += 4;
    }

    y += 4;

    // 🔹 Pagamento (detalhado)
    const pg = reserva.pagamentosData;

    // Valor Total
    const valorTotal = pg?.valorTotal ?? reserva.valorTotal ?? 0;

    // Cálculos de desconto
    const descontoEscopo = pg?.descontoEscopo || "total";
    const descontoPercentual = pg?.descontoPercentual ?? 0;

    // Desconto na entrada (se aplicável)
    const valorEntradaBruto = pg?.valorEntrada ?? 0;
    const valorEntradaComDesconto =
      pg?.valorEntradaComDesconto ?? valorEntradaBruto;
    const descontoNaEntrada =
      descontoEscopo === "entrada"
        ? Math.max(0, valorEntradaBruto - valorEntradaComDesconto)
        : 0;

    // Valor total com desconto (se desconto no total)
    const valorComDesconto = pg?.valorComDesconto ?? valorTotal;
    const descontoNoTotal =
      descontoEscopo === "total"
        ? Math.max(0, valorTotal - valorComDesconto)
        : 0;
    const valorLiquido =
      descontoEscopo === "total" ? valorComDesconto : valorTotal;

    // Entrada exibida (considerando desconto)
    const valorEntradaExibida =
      descontoEscopo === "entrada"
        ? valorEntradaComDesconto
        : valorEntradaBruto;
    const formaPagamentoEntrada = pg?.formaPagamentoEntrada || "";

    // 2ª Forma de pagamento
    const usa2 = pg?.usaDuasFormas ?? false;
    const valor2Bruto = pg?.valorFormaPagamento2 ?? 0;
    const descontoPercentualForma2 = pg?.descontoPercentualForma2 ?? 0;
    const valor2ComDesconto =
      pg?.valorFormaPagamento2ComDesconto ?? valor2Bruto;
    const descontoForma2 = Math.max(0, valor2Bruto - valor2ComDesconto);
    const formaPagamento2 = pg?.formaPagamento2 || "";
    const colEsq = m;
    const colDir = w / 2 + 5; // metade da página

    // Total pago na entrada
    const totalPagoEntrada = valorEntradaExibida + valor2ComDesconto;

    // Saldo Devedor
    const saldoDevedor =
      pg?.saldoDevedor ?? Math.max(0, valorLiquido - totalPagoEntrada);

    y = addSection(doc, "Pagamento", y, m, w);

    // ===== LINHA 1 =====
    doc.text(`Valor Total: ${formatCurrency(valorTotal)}`, colEsq, y);

    if (valorEntradaExibida > 0) {
      doc.text(
        `1ª Forma (${getFormaPagamentoLabel(
          formaPagamentoEntrada
        )}): ${formatCurrency(valorEntradaExibida)}`,
        colDir,
        y
      );
    }
    y += 5;

    // ===== LINHA 2 =====
    if (descontoEscopo === "entrada" && descontoNaEntrada > 0) {
      doc.text(
        `Desc. Entrada (${descontoPercentual}%): - ${formatCurrency(
          descontoNaEntrada
        )}`,
        colEsq,
        y
      );
    }

    if (usa2 && valor2Bruto > 0) {
      doc.text(
        `2ª Forma (${getFormaPagamentoLabel(
          formaPagamento2
        )}): ${formatCurrency(valor2Bruto)}`,
        colDir,
        y
      );
    }
    y += 5;

    // ===== LINHA 3 =====
    if (descontoEscopo === "total" && descontoNoTotal > 0) {
      doc.text(
        `Desc. Total (${descontoPercentual}%): - ${formatCurrency(
          descontoNoTotal
        )}`,
        colEsq,
        y
      );
    }

    if (usa2 && descontoForma2 > 0) {
      doc.text(
        `Desc. 2ª Forma (${descontoPercentualForma2}%): - ${formatCurrency(
          descontoForma2
        )}`,
        colDir,
        y
      );
    }
    y += 5;

    // ===== VALOR LÍQUIDO (se desconto no total) =====
    if (descontoEscopo === "total" && descontoNoTotal > 0) {
      doc.setFont("helvetica", "bold");
      doc.text(`Valor Líquido: ${formatCurrency(valorLiquido)}`, colEsq, y);
      doc.setFont("helvetica", "normal");
      y += 5;
    }

    y += 2;

    // ===== TOTAL PAGO + SALDO (MESMA LINHA) =====
    doc.setFont("helvetica", "bold");
    doc.text(
      `Total Pago (Entrada): ${formatCurrency(totalPagoEntrada)}`,
      colEsq,
      y
    );

    doc.text(`Saldo Devedor: ${formatCurrency(saldoDevedor)}`, colDir, y);

    doc.setFont("helvetica", "normal");
    y += 8;

    // ===== LINHA SEPARADORA =====
    doc.line(m, y, w - m, y);
    y += 6;

    // 🔹 Mensagem personalizada (texto livre do usuário)
    const msg = await getMensagemPerfil();

    if (msg) {
      // y = addSection(doc, 'Mensagem do Relatório', y, m, w);

      const lines = doc.splitTextToSize(msg, w - m * 2);

      lines.forEach((line: string) => {
        // Quebra de página automática
        if (y > h - 20) {
          doc.addPage();
          y = m;
        }

        doc.text(line, m, y);
        y += 3.5;
      });

      y += 4;
    }

    // 🔹 Assinatura
    // y += 4;
    // doc.text("Assinatura do Cliente", m, y);
    y += 20;
    doc.line(m, y, w - m, y);
    y += 4;
    doc.text("Assinatura do Cliente", w / 2, y, { align: "center" });

    // 🔹 Rodapé
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text("Documento gerado pelo sistema", w / 2, h - 10, {
      align: "center",
    });

    try {
      doc.save(`contrato_${numero}.pdf`);
    } catch (e) {
      toast.error("Erro ao salvar PDF");
    }
  };

  return (
    <Button
      btnLabel="Baixar Contrato"
      btnColor="warning"
      onClick={generatePDF}
      extraCss={`d-flex align-items-center gap-2 ${className || ""} text-white`}
    >
      <FileText size={20} />
    </Button>
  );
};

export default RelatorioButton;
